/**
 * Top-of-page summary block: Last run, 3-column patient/insurance/policy info,
 * and verification footer. Matches the PDF blob generator and appears in DOM-captured PDFs.
 * Data from V4 effective plan (selectedPlanAtom).
 */

import { Trans } from '@lingui/react/macro';
import { useAtomValue } from 'jotai';

import { parseAndFormatDate } from '../../../utils/parseDate';
import {
  selectedNetworkIndexAtom,
  selectedPlanAtom,
  selectedPlanRawAtom,
} from '../atoms';
import { getNetworksForPlan } from '../helpers';
import { formatClaimAddress } from './helpers/formatClaimAddress';

const formatSummaryDate = (dateString: string | null | undefined): string => {
  if (!dateString) return 'N/A';
  try {
    return parseAndFormatDate(dateString, 'MMM d, yyyy') || 'N/A';
  } catch {
    return 'N/A';
  }
};

const formatPeriod = (
  start: string | null | undefined,
  end: string | null | undefined,
): string => {
  const s = start ? formatSummaryDate(start) : 'N/A';
  const e = end ? formatSummaryDate(end) : 'N/A';
  return `${s} - ${e}`;
};


const ROW_LABELS = [
  ['PATIENT NAME', 'MEMBER ID', 'SUBSCRIBER ID', 'SUBSCRIBER DOB', 'PAYER ID'],
  ['INSURANCE', 'PLAN NAME', 'PLAN TYPE', 'GROUP', 'CLAIMS ADDRESS'],
  ['BENEFIT PERIOD', 'BENEFIT PERIOD TYPE', 'POLICY PERIOD', 'NETWORK'],
] as const;

export const InsuranceBenefitsSummaryHeader = () => {
  const plan = useAtomValue(selectedPlanAtom);
  const planRaw = useAtomValue(selectedPlanRawAtom);
  const networkIndex = useAtomValue(selectedNetworkIndexAtom);
  const networks = getNetworksForPlan(planRaw);
  const networkName = networks[networkIndex]?.name ?? networks[0]?.name ?? null;
  if (!plan) return null;

  const lastRunStr = formatSummaryDate(plan.lastUpdated ?? null);

  const col1 = [
    plan.patient?.displayName ?? 'N/A',
    plan.patient?.memberId ?? 'N/A',
    plan.subscriber?.id ?? 'N/A',
    formatSummaryDate(plan.subscriber?.dateOfBirth ?? null),
    plan.commonPayerId ?? (plan.payerId != null ? String(plan.payerId) : 'N/A'),
  ];
  const col2 = [
    plan.payerName ?? plan.plan?.payerName ?? 'N/A',
    plan.plan?.groupName ?? 'N/A',
    plan.plan?.planType ?? 'N/A',
    [plan.plan?.groupNumber, plan.plan?.groupName]
      .filter(Boolean)
      .join(' - ') || 'N/A',
    formatClaimAddress(plan.claimsAddress),
  ];
  const col3 = [
    formatPeriod(plan.plan?.effectiveDate, plan.plan?.endDate),
    plan.plan?.benefitPeriodType ?? 'N/A',
    formatPeriod(plan.plan?.policyStartDate, plan.plan?.policyEndDate),
    networkName ?? plan.plan?.networkStatus ?? 'N/A',
  ];

  const sourceLabel = plan.resultSource ?? plan.source ?? 'N/A';
  const verifiedDate = formatSummaryDate(plan.lastUpdated ?? null);

  return (
    <div className="vai-mb-8 vai-rounded-md vai-border vai-border-gray-200 vai-bg-white vai-p-5 vai-shadow-sm">
      {/* Last run */}
      <p className="vai-mb-4 vai-text-sm vai-text-gray-700">
        <Trans>Last run</Trans>: {lastRunStr}
      </p>

      {/* Three columns */}
      <div className="vai-grid vai-grid-cols-3 vai-gap-6">
        {[col1, col2, col3].map((col, colIndex) => (
          <div key={colIndex} className="vai-space-y-3">
            {ROW_LABELS[colIndex].map((label, rowIndex) => (
              <div key={rowIndex}>
                <p className="vai-text-xs vai-font-medium vai-uppercase vai-text-gray-500">
                  {label}
                </p>
                <p className="vai-text-sm vai-font-semibold vai-text-gray-900">
                  {col[rowIndex]}
                </p>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Footer: Source | Verified date */}
      <div className="vai-mt-4 vai-border-t vai-border-gray-200 vai-pt-3">
        <p className="vai-text-xs vai-text-gray-500">
          <Trans>Source</Trans>: {sourceLabel} | <Trans>Verified</Trans>:{' '}
          {verifiedDate}
        </p>
      </div>
    </div>
  );
};
